import { Autocomplete, Chip, TextField } from '@mui/material';
import * as React from 'react';
import { useUserTags } from '../../../../../sic-lib/api/hooks/useUserTags';
import { ContactType } from '../../../../../sic-lib/enums/ContactType';
import { FieldDataType } from '../../../../../sic-lib/enums/FieldDataType';
import { ConversationGridFiltersActions } from '../../../../../sic-lib/reducers/conversationGridFiltersReducer';
import { ConversationGridFiltersItem } from '../../../../../sic-lib/types/ConversationGridFiltersItem';
import { IErrorFormValidation } from '../../../../../sic-lib/utils/formValidationHelpers';
import { useAppSelector } from '../../../../../store/hooks';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

type FieldValueName = 'value' | 'values';

interface Props {
    config: ConversationGridFiltersItem;
    formDispatch: React.Dispatch<ConversationGridFiltersActions>;
    formError: IErrorFormValidation | null;
}

export default function FilterFastFieldSelect({ config, formDispatch, formError }: Props) {
    const [option, setOption] = React.useState<string[]>([]);

    const { data: tagData } = useUserTags();

    const userTags = React.useMemo(() => {
        return tagData || [];
    }, [tagData]);

    const users = useAppSelector(state => state.user.users);

    React.useEffect(() => {
        switch (config.fieldType) {
            case FieldDataType.tags:
                setOption(userTags.map(tag => tag.tag));
                break;
            case FieldDataType.users:
                setOption(users.map(user => user.userName));
                break;
            case FieldDataType.enum:
                setOption(Object.keys(ContactType).filter(key => isNaN(Number(key))));
                break;
            default:
                setOption(Object.keys(ContactType).filter(key => isNaN(Number(key))));
        }

        return () => {
            setOption([]);
        };
    }, [config.fieldType]);

    const handleChange = (event: React.SyntheticEvent, value: string[]) => {
        formDispatch({
            type: 'MODIFY_FORM_FILTER',
            fieldId: config.fieldId,
            condition: {
                values: value,
            },
        });
    };

    return (
        <Autocomplete
            multiple={true}
            id={`multiple-checkbox-${config.fieldId}`}
            options={option}
            value={config.values}
            renderTags={(value: readonly string[], getTagProps) => {
                return value.map((option: string, index: number) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ));
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label={config.fieldName}
                    error={
                        formError &&
                        formError.fieldId === config.fieldId &&
                        formError.field === 'values'
                            ? true
                            : false
                    }
                    helperText={
                        formError &&
                        formError.fieldId === config.fieldId &&
                        formError.field === 'values'
                            ? formError.message
                            : ''
                    }
                />
            )}
            onChange={handleChange}
        />
    );
}
